import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  OpportuniteActiviteRow,
  OppActiviteStatut,
  OppActiviteType,
} from '@/lib/database.types';

export function useOppActivites(opportuniteId: string | null) {
  return useQuery({
    queryKey: ['crm_opp_activites', opportuniteId],
    enabled: !!opportuniteId,
    queryFn: async (): Promise<OpportuniteActiviteRow[]> => {
      if (!opportuniteId) return [];
      const { data, error } = await supabase
        .from('opportunites_activites')
        .select('*')
        .eq('opportunite_id', opportuniteId)
        .order('date_prevue', { ascending: false, nullsFirst: false })
        .limit(200);
      if (error) throw error;
      return data as unknown as OpportuniteActiviteRow[];
    },
  });
}

export function useMyOppActivites(filter: {
  assigneeId?: string | null;
  statut?: OppActiviteStatut | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}) {
  return useQuery({
    queryKey: ['crm_my_activites', filter],
    queryFn: async (): Promise<OpportuniteActiviteRow[]> => {
      let q = supabase
        .from('opportunites_activites')
        .select('*')
        .order('date_prevue', { ascending: true, nullsFirst: false })
        .limit(500);
      if (filter.assigneeId) q = q.eq('assignee_id', filter.assigneeId);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.dateFrom) q = q.gte('date_prevue', filter.dateFrom);
      if (filter.dateTo) q = q.lte('date_prevue', filter.dateTo);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as OpportuniteActiviteRow[];
    },
  });
}

export function useUpsertOppActivite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      opportunite_id: string;
      type: OppActiviteType;
      sujet?: string | null;
      description?: string | null;
      date_prevue?: string | null;
      date_realisee?: string | null;
      duree_minutes?: number | null;
      statut?: OppActiviteStatut;
      assignee_id?: string | null;
      fichier_url?: string | null;
    }): Promise<OpportuniteActiviteRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('opportunites_activites')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as OpportuniteActiviteRow;
      }
      const { data, error } = await supabase
        .from('opportunites_activites')
        .insert(rest)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as OpportuniteActiviteRow;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['crm_opp_activites', vars.opportunite_id] });
      void qc.invalidateQueries({ queryKey: ['crm_my_activites'] });
    },
  });
}

export function useDeleteOppActivite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('opportunites_activites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm_opp_activites'] });
      void qc.invalidateQueries({ queryKey: ['crm_my_activites'] });
    },
  });
}
